import uniqWith from 'lodash/uniqWith'
import {
  SubstitutionForProductState,
  type DoNotSub,
  type RetrievedPreference,
  type RetrievedPreferenceAPI,
  type SubstitutionsActionPayload,
  type SubstitutionsForProductResponse,
  type SubstitutionsForProductsPayload,
  type SubstitutionsForProductsResponse,
} from '@/features/shop/services/Substitutions/types'
import { type Product } from '@/features/shop/services/Product/types'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { BACKUP_PREFERENCE_SELECTION } from '@/features/shared/constants/global'

export const normalizePreferenceResponse = (
  preference: RetrievedPreferenceAPI = {},
): RetrievedPreference => {
  if ('product_id' in preference && Boolean(preference.product_id)) {
    return normalizeProduct(preference)
  }
  if ('do_not_sub' in preference && preference.do_not_sub) {
    return { do_not_sub: true }
  }
  return {}
}

export const generateSubstitutionsPayload = (
  productId: number,
  { preferred = [], personalized = [], generic = [], state }: SubstitutionsForProductResponse,
): SubstitutionsActionPayload => {
  const preferredSubstitution = normalizePreferenceResponse(preferred?.[0] ?? {})
  const substitutions = uniqWith(
    [
      ...(personalized ?? []).map((product) => normalizeProduct(product)),
      ...(generic ?? []).map((product) => normalizeProduct(product)),
    ],
    (product, nextProduct) => product.id === nextProduct.id,
  )
  return {
    productId,
    preferredSubstitution,
    substitutions,
    state: state ?? SubstitutionForProductState.available,
  }
}

export const generateSubstitutionsForProductsPayload = (
  response: SubstitutionsForProductsResponse,
): SubstitutionsForProductsPayload => {
  const payload = { substitutionsById: {}, preferences: {} }
  const subs = response.substitutions
  if (!subs) return payload
  return Object.keys(subs).reduce<SubstitutionsForProductsPayload>((acc, id) => {
    const { state, substitutions, preferredSubstitution } = generateSubstitutionsPayload(
      Number(id),
      subs[id] || {},
    )
    return {
      substitutionsById: {
        ...acc.substitutionsById,
        [id]: { substitutions, state },
      },
      preferences: {
        ...acc.preferences,
        [id]: preferredSubstitution,
      },
    }
  }, payload)
}

export const getIsProductPreference = (
  preference: RetrievedPreference = {},
): preference is Product => 'id' in preference && Boolean(preference.id)

export const getIsDoNotSub = (preference: RetrievedPreference = {}): preference is DoNotSub =>
  'do_not_sub' in preference && Boolean(preference.do_not_sub)

export const getBackupPreferenceText = (preference: RetrievedPreference) => {
  const { CONTACT_ME, DO_NOT_SUBSTITUTE, GET_A_BACKUP_ITEM } = BACKUP_PREFERENCE_SELECTION
  if (getIsDoNotSub(preference)) return DO_NOT_SUBSTITUTE
  if (getIsProductPreference(preference)) return GET_A_BACKUP_ITEM
  return CONTACT_ME
}

export const getIsProductCurrentPreference = (
  product: { id: number },
  preference: RetrievedPreference,
) => getIsProductPreference(preference) && preference.id === product.id
